import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  HbrDivider,
  HbrForm,
  HbrInput,
  HbrOption,
  HbrSelect,
  HbrSpinner,
  HbrSwitch,
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
  HbrTextarea,
} from '@harbor/elements-react-standalone';

import { MoodleUsersSelect } from './MoodleUsersSelect';
import { MoodleSpeakersSelect } from './MoodleSpeakersSelect';
import { MoodleLocationsSelect } from './MoodleLocationsSelect';
import { MoodleAgendaTable } from './MoodleAgendaTable';
import { MoodleGalleryTable } from './MoodleGalleryTable';

import {
  useJoomlaContent,
  useJoomlaUserSearch,
  useMoodleEventSpeakers,
  useMoodleEventSponsors,
  UserLike,
} from '../../../hooks/useMoodleEventBooking';
import { useMoodleCourseEnrollments } from '../../../hooks/useMoodleHooks';
import { RootState } from '../../../support/store';

import { MoodleAgendaItem, MoodleLocation } from '../../../types/moodleEventBooking';
import { hydrateEvents } from '../../../support/slices/moodleSlice';

import { fetchItem, postItem, putItem } from '../../../support/queries';
import { rewriteTemplateHtml } from '../../../support/moodleHelpers';
import { useFetchSfCustomerById } from '../../../hooks/salesforce-hooks';
import StbModal from '../../security-toolbox-ui/StbModal';
import {
  extractTimeZoneId,
  findLabelAcrossRegions,
  getNextHourIsoLocal,
  isEqual,
  matchTimezoneLabel,
  roundToNext5Min,
} from '../../../support/utils';
import { timezoneMap } from '../../../support/timezoneMapping';
import StbButton from '../../security-toolbox-ui/StbButton';
import { renderErrorMessage } from '../../security-toolbox-ui/security-toolbox-ui-utils';
import { CacheKey } from '../../../types/cache-keys';

function toAlias(title: string): string {
  return (title || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function asArray<T = any>(obj: Record<number, T> | undefined): T[] {
  return obj ? Object.values(obj) : [];
}

function mapEventToForm(ev: any, speakerMap: Record<number, any>, sponsorMap: Record<number, any>) {
  const ownersUsers = Array.isArray(ev.owners) ? ev.owners.map((o: any) => o.user) : [];
  const ownerIds = Array.isArray(ev.owners)
    ? ev.owners.map((o: any) => Number(o?.user?.id ?? o?.user_id)).filter((n: any) => Number.isFinite(n))
    : [];

  return {
    title: ev.title || '',
    alias: ev.alias || '',
    event_owners: ownersUsers,
    event_owner_ids: ownerIds as number[],
    category_id: String(ev.category?.category_id ?? ''),
    main_category_id: String(ev.main_category_id ?? ev.category?.main_category ?? ''),
    location_id: String(ev.location_id ?? ''),
    sl1: ev?.sl1 ?? ev.custom_fields?.sl1 ?? '',
    event_date: ev.event_date ? ev.event_date.slice(0, 16) : '',
    event_end_date: ev.event_end_date ? ev.event_end_date.slice(0, 16) : '',
    timezone: ev?.timezone ?? '',
    tz_offset: ev.tz_offset ?? '',
    event_capacity: ev.event_capacity ?? '',
    waiting_list_capacity: ev.waiting_list_capacity ?? '',
    field_hours: ev.custom_fields?.field_hours ?? '',
    event_type: ev.custom_fields?.event_type ?? 'sales',
    deal_id: ev.custom_fields?.deal_id ?? '',
    webex: ev.custom_fields?.webex ?? '',
    course_id: String(ev.params?.joomdle_courses ?? ''),
    use_description_template: ev.custom_fields?.use_article_desc === '1',
    template_id: ev.custom_fields?.article_id ?? '',
    short_description: ev.short_description ?? '',
    cut_off_date: ev.cut_off_date ? ev.cut_off_date.slice(0, 16) : '',
    proctor: ev.custom_fields?.proctor ?? undefined,
    proctors: Array.isArray(ev.custom_fields?.proctors)
      ? ev.custom_fields?.proctors
      : typeof ev.custom_fields?.proctors === 'string'
        ? [ev.custom_fields?.proctors]
        : [],
    proctor_id: undefined as number | undefined,
    speakers: Array.isArray(ev.speakers)
      ? ev.speakers.map((s: any) => speakerMap[s.speaker]).filter(Boolean)
      : [],
    agenda: ev.agendas ?? [],
    gallery: ev.gallery ?? [],
    sponsors: Array.isArray(ev.sponsors)
      ? ev.sponsors.map((s: any) => sponsorMap[s.sponsor]).filter(Boolean)
      : [],
  };
}

export function CreateEventModal({
                                   eventId,
                                   onCreated,
                                   courseId: propCourseId,
                                   customerId = '',
                                   opportunityId = '',
                                   onSaveRegister,
                                   setSubmitDisabled,
                                 }: {
  eventId?: string | number;
  onCreated: (saved?: any) => void;
  courseId?: number | string;
  customerId?: string;
  opportunityId?: string;
  onSaveRegister: (saveFn: () => Promise<void>) => void;
  setSubmitDisabled: (disabled: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const MOODLE_INSTANCE = 'external' as const;

  const [activePanel, setActivePanel] = useState<'eventInfo' | 'eventDetails' | 'additionalInfo'>('eventInfo');
  const [userEditedTitle, setUserEditedTitle] = useState(false);

  const { data: customerData } = useFetchSfCustomerById(customerId);
  const customerName = customerData?.Name || '';

  const nextHour = getNextHourIsoLocal();

  const initialForm = {
    title: '',
    alias: '',
    event_owners: [] as any[],
    event_owner_ids: [] as number[],
    category_id: '',
    main_category_id: '',
    location_id: '',
    sl1: '',
    event_date: nextHour,
    event_end_date: nextHour,
    timezone: '',
    tz_offset: '',
    event_capacity: '',
    waiting_list_capacity: '',
    field_hours: '',
    event_type: 'sales',
    deal_id: '',
    webex: '',
    course_id: String(propCourseId ?? ''),
    use_description_template: false,
    template_id: '',
    short_description: '',
    cut_off_date: nextHour,
    proctor: undefined as any,
    proctors: [] as string[],
    proctor_id: undefined as number | undefined,
    speakers: [] as any[],
    agenda: [] as any[],
    gallery: [] as any[],
    sponsors: [] as any[],
  };

  const [form, setForm] = useState<any>(initialForm);

  useEffect(() => {
    const validated =
      form?.title !== '' &&
      form?.category_id !== '' &&
      form?.sl1 !== '' &&
      form?.timezone !== '' &&
      form?.event_date !== '' &&
      form?.event_end_date !== '' &&
      (form?.use_description_template ? form.template_id !== '' : true);

    setSubmitDisabled(!validated);
  }, [setSubmitDisabled, form]);

  const moodleState: any = useSelector(
    (state: RootState) => state.moodle.instances.external as any
  );

  const courseIdForTitle = Number(form.course_id) || Number(propCourseId);
  const courseTitle = useMemo(() => {
    if (!courseIdForTitle) return '';
    const course = moodleState?.courses?.[courseIdForTitle];
    return course ? course.fullname || course.shortname || course.title || '' : '';
  }, [moodleState, courseIdForTitle]);

  function getDefaultTitle() {
    if (userEditedTitle || (eventId && form.title)) return form.title;
    if (customerId && customerName && courseTitle) {
      return `${customerName} - ${courseTitle}`.replace(/^- | -$/g, '').trim();
    }
    if (courseTitle && !customerId) return courseTitle;
    return '';
  }

  const joomlaCategories = asArray<any>(moodleState?.joomlaCategories);
  const joomlaEventBookingCategories = asArray<any>(moodleState?.joomlaEventBookingCategories);

  const [joomlaUser, setJoomlaUser] = useState<UserLike | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const {
    data: eventSpeakersData,
    isLoading: eventSpeakersLoading,
  } = useMoodleEventSpeakers(Number(eventId), undefined, undefined, { enabled: !!eventId });

  const eventSpeakers = useMemo(() => eventSpeakersData?.items ?? [], [eventSpeakersData]);

  const {
    data: eventSponsorsData,
    isLoading: eventSponsorsLoading,
  } = useMoodleEventSponsors(Number(eventId), undefined, undefined, { enabled: !!eventId });

  const eventSponsors = useMemo(() => eventSponsorsData?.items ?? [], [eventSponsorsData]);

  const userId = moodleState?.user?.id;
  const userEmail = useSelector((state: RootState) => state.user.userData.email);
  const currentUsername = userEmail ? userEmail.split('@')[0] : '';

  const { data: joomlaUserSearch = [] } = useJoomlaUserSearch(
    {
      user_ids: [],
      usernames: [currentUsername],
      emails: [],
      limit: 10,
    },
    {
      enabled: !!currentUsername,
    }
  );

  const hydratedEvent = eventId ? moodleState?.events?.[Number(eventId)] : undefined;
  const sponsorMap = useMemo(() => moodleState?.sponsors || {}, [moodleState?.sponsors]);
  const speakerMap = useMemo(() => moodleState?.speakers || {}, [moodleState?.speakers]);

  const templateCategoryId = useMemo(
    () => joomlaCategories.find((cat: any) => cat.path === 'workshop-short-description-templates')?.id,
    [joomlaCategories]
  );

  const { data: templateOptionsData } = useJoomlaContent(templateCategoryId);
  const templateOptions = useMemo(() => templateOptionsData?.items ?? [], [templateOptionsData?.items]);

  const [useCutoff, setUseCutoff] = useState<boolean>(false);
  const [useWaitingList, setUseWaitingList] = useState<boolean>(false);

  const [cachedManualShort, setCachedManualShort] = useState<string>('');
  const [cachedTemplateId, setCachedTemplateId] = useState<string>('');
  const [cachedTemplateHtml, setCachedTemplateHtml] = useState<string>('');
  const [userEditedSl1, setUserEditedSl1] = useState(false);

  useEffect(() => {
    if (propCourseId) {
      setForm((f: any) => ({ ...f, course_id: String(propCourseId) }));
    }
  }, [propCourseId]);

  useEffect(() => {
    if (joomlaUserSearch.length && !joomlaUser) {
      setJoomlaUser(joomlaUserSearch[0]);
    }
  }, [joomlaUserSearch, joomlaUser]);

  useEffect(() => {
    if (!userEditedTitle && !eventId && !form.title) {
      if ((customerId ? !!customerName : true) && (courseIdForTitle ? !!courseTitle : true)) {
        setForm((f: any) => ({ ...f, title: getDefaultTitle() }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerId,
    customerName,
    courseTitle,
    propCourseId,
    form.course_id,
    userEditedTitle,
    eventId,
    form.title,
    courseIdForTitle,
  ]);

  const courseIdNum = useMemo(
    () => Number(form.course_id || propCourseId) || 0,
    [form.course_id, propCourseId]
  );

  const { data: enrollments = [] } = useMoodleCourseEnrollments(
    courseIdNum,
    null,
    { enabled: courseIdNum > 0 },
    MOODLE_INSTANCE
  );

  const proctorUsers = useMemo(
    () =>
      enrollments.filter(
        (u: any) =>
          Array.isArray(u.roles) &&
          u.roles.some(
            (r: any) =>
              r.shortname === 'proctor' || r.name === 'Proctor (non-editing role)'
          )
      ),
    [enrollments]
  );

  const preselectedOwners = form.event_owners;

  const selectedProctorId: number | string | undefined = useMemo(() => {
    if (form.proctor_id != null && form.proctor_id !== '') return form.proctor_id;
    const p = form.proctor;
    if (!p) return undefined;
    if (typeof p === 'object' && p.id != null) return p.id;
    if (typeof p === 'number' || (!isNaN(Number(p)) && typeof p !== 'object')) return Number(p);
    if (typeof p === 'string') {
      const found = proctorUsers.find((u: any) => u.username === p || u.email === p);
      return found?.id;
    }
    return undefined;
  }, [form.proctor_id, form.proctor, proctorUsers]);

  useEffect(() => {
    if (form.proctor_id) return;
    const p = form.proctor;
    if (!p) return;

    let found: any | undefined;
    if (typeof p === 'object' && p.id != null) {
      found = proctorUsers.find((u: any) => Number(u.id) === Number(p.id));
    } else if (typeof p === 'number' || (!isNaN(Number(p)) && typeof p !== 'object')) {
      found = proctorUsers.find((u: any) => Number(u.id) === Number(p));
    } else if (typeof p === 'string') {
      found = proctorUsers.find((u: any) => u.username === p || u.email === p);
    }

    if (found?.id && form.proctor_id !== found.id) {
      setForm((f: any) => ({ ...f, proctor_id: Number(found.id) }));
    }
  }, [form.proctor, form.proctor_id, proctorUsers]);

  useEffect(() => {
    if (!eventId) return;
    if (hydratedEvent) return;

    fetchItem(`moodle/joomla/event/${eventId}/`)
      .then((eventData) => {
        dispatch(hydrateEvents([eventData] as any));
      })
      .catch((err) => {
        console.error('Failed to fetch event', err);
      });
  }, [eventId, dispatch, hydratedEvent]);

  useEffect(() => {
    const start = form.event_date || '';
    const cutoff = form.cut_off_date || '';
    setUseCutoff(Boolean(start && cutoff && cutoff !== start));
  }, [form.event_date, form.cut_off_date]);

  useEffect(() => {
    const wl = Number(form.waiting_list_capacity || 0);
    setUseWaitingList(wl > 0);
  }, [form.waiting_list_capacity]);

  const allLoaded =
    (!!hydratedEvent || !eventId) &&
    (!eventId || (!eventSpeakersLoading && !eventSponsorsLoading));

  const timezones = useMemo(() => (form.sl1 ? timezoneMap[form.sl1] : []), [form.sl1]);

  useEffect(() => {
    if (!eventId) return;
    if (!allLoaded) return;

    const mapped = mapEventToForm(
      {
        ...hydratedEvent,
        speakers: eventSpeakers,
        sponsors: eventSponsors,
        ...(userEditedSl1
          ? { sl1: form.sl1, tz_offset: form.tz_offset, timezone: form.timezone }
          : {}),
      },
      speakerMap,
      sponsorMap
    );

    if ((!mapped.event_owner_ids || mapped.event_owner_ids.length === 0) && userId) {
      mapped.event_owner_ids = [Number(userId)];
    }

    if (propCourseId) {
      mapped.course_id = String(propCourseId);
    }

    const tzLabel =
      matchTimezoneLabel(mapped.tz_offset, form.sl1 ? timezoneMap[form.sl1] : []) ||
      findLabelAcrossRegions(mapped.tz_offset);

    mapped.timezone = tzLabel || '';

    setCachedManualShort(mapped.short_description || '');

    if (mapped.use_description_template && mapped.template_id) {
      setCachedTemplateId(String(mapped.template_id));
      const selected = templateOptions.find((t: any) => String(t.id) === String(mapped.template_id));
      setCachedTemplateHtml(rewriteTemplateHtml(selected?.introtext || ''));
    }

    mapped.alias = toAlias(mapped.title || '');

    setForm(mapped);
  }, [
    allLoaded,
    hydratedEvent,
    eventSpeakers,
    eventSponsors,
    eventId,
    form.sl1,
    speakerMap,
    sponsorMap,
    userId,
    propCourseId,
    templateOptions,
    customerName,
    customerId,
    userEditedSl1,
    form.tz_offset,
    form.timezone,
  ]);

  useEffect(() => {
    if (form.event_date && form.event_end_date) {
      const start = new Date(form.event_date);
      const end = new Date(form.event_end_date);
      const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      setForm((f: any) => ({ ...f, field_hours: diff > 0 ? diff.toString() : '' }));
    }
  }, [form.event_date, form.event_end_date]);

  useEffect(() => {
    if (form.timezone !== '') {
      const id = extractTimeZoneId(form.timezone);
      if (id && id !== form.tz_offset) {
        setForm((f: any) => ({ ...f, tz_offset: id }));
      }
    }
  }, [form.timezone, form.tz_offset]);

  useEffect(() => {
    if (!timezones?.length || form.timezone === '') return;
    const inRegion = timezones.includes(form.timezone);
    if (!inRegion) {
      const fallback = matchTimezoneLabel(form.tz_offset, timezones);
      if (fallback !== form.timezone) {
        setForm((f: any) => ({ ...f, timezone: fallback || '' }));
      }
    }
  }, [timezones, form.timezone, form.tz_offset]);

  useEffect(() => {
    if (form.use_description_template && form.template_id && templateOptions.length) {
      const selected = templateOptions.find((opt: any) => String(opt.id) === String(form.template_id));
      const html = rewriteTemplateHtml(selected?.introtext || '');
      setCachedTemplateId(String(form.template_id));
      setCachedTemplateHtml(html);
      setForm((f: any) => ({ ...f, short_description: selected?.introtext ?? '' }));
    }
  }, [form.use_description_template, form.template_id, templateOptions]);

  const handleInput = (e: any) => {
    const { name, value, type, checked } = e.target;
    let nextVal = type === 'checkbox' ? checked : value;

    if (['event_date', 'event_end_date', 'cut_off_date'].includes(name)) {
      nextVal = roundToNext5Min(nextVal);
    }

    if (name === 'title') setUserEditedTitle(true);

    setForm((f: any) => ({
      ...f,
      [name]: nextVal,
      ...(name === 'title' ? { alias: toAlias(String(nextVal)) } : {}),
    }));

    if (name === 'short_description' && !form.use_description_template) {
      setCachedManualShort(String(nextVal || ''));
    }
  };

  const isLoading =
    (customerId && !customerName) ||
    (courseIdForTitle && !courseTitle) ||
    (eventId && !allLoaded);

  const handleSelectChange = (name: string) => (e: any) => {
    const value = e.detail?.value ?? e.target?.value ?? '';
    if (name === 'sl1') {
      setUserEditedSl1(true);
      setForm((f: any) => ({ ...f, [name]: value, timezone: '', tz_offset: '' }));
    } else {
      setForm((f: any) => ({ ...f, [name]: value }));
    }
  };

  const handleOwnersChange = (ids: number[] | number | null) => {
    const nextIds = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
    if (!isEqual(form.event_owner_ids, nextIds)) {
      setForm((f: any) => ({ ...f, event_owner_ids: nextIds }));
    }
  };

  const handleProctorSelectInput = (e: any) => {
    const value = e.detail?.value ?? e.target?.value ?? '';
    const nextId = value ? Number(value) : undefined;
    setForm((f: any) => ({ ...f, proctor_id: nextId, proctor: undefined }));
  };

  const handleGalleryChange = (galleryChanged: any[]) =>
    setForm((f: any) => ({
      ...f,
      gallery: galleryChanged,
    }));

  const handleLocationChange = (location: MoodleLocation | null) =>
    setForm((f: any) => ({
      ...f,
      location_id: location?.id || '',
    }));

  const handleAgendaChange = (agendaChanged: MoodleAgendaItem[]) =>
    setForm((f: any) => ({
      ...f,
      agenda: agendaChanged,
    }));

  const handleUseTemplateToggle = (e: any) => {
    const checked = e.detail?.checked ?? e.target?.checked ?? false;

    if (checked) {
      setCachedManualShort(form.short_description || '');
      setForm((prev: any) => {
        const nextTemplateId = prev.template_id || cachedTemplateId || '';
        let nextShort = prev.short_description;

        if (nextTemplateId) {
          const selected = templateOptions.find((t: any) => String(t.id) === String(nextTemplateId));
          nextShort = selected?.introtext ?? '';
          setCachedTemplateHtml(rewriteTemplateHtml(selected?.introtext || ''));
          setCachedTemplateId(String(nextTemplateId));
        } else {
          nextShort = '';
        }

        return {
          ...prev,
          use_description_template: true,
          template_id: nextTemplateId,
          short_description: nextShort,
        };
      });
    } else {
      setForm((prev: any) => ({
        ...prev,
        use_description_template: false,
        short_description: cachedManualShort,
      }));
    }
  };

  const handleTemplateChange = (e: any) => {
    const value = e.detail?.value ?? e.target?.value ?? '';
    const selected = templateOptions.find((t: any) => String(t.id) === String(value));
    const html = rewriteTemplateHtml(selected?.introtext || '');
    setCachedTemplateId(String(value));
    setCachedTemplateHtml(html);
    setForm((f: any) => ({ ...f, template_id: value, short_description: selected?.introtext ?? '' }));
  };

  const handleUseCutoffToggle = (e: any) => {
    const checked = e.detail?.checked ?? e.target?.checked ?? false;
    setUseCutoff(checked);
    if (!checked) {
      setForm((f: any) => ({ ...f, cut_off_date: f.event_date || '' }));
    }
  };

  const handleUseWaitingListToggle = (e: any) => {
    const checked = e.detail?.checked ?? e.target?.checked ?? false;
    setUseWaitingList(checked);
    setForm((f: any) => ({
      ...f,
      waiting_list_capacity: checked ? f.waiting_list_capacity || '' : '0',
    }));
  };

  const handleSave = useCallback(async () => {
    const tzLabel = form.timezone || (timezones?.length ? timezones[0] : '');
    const tzId = extractTimeZoneId(tzLabel);

    const owners: number[] = (form.event_owner_ids || [])
      .map((n: any) => Number(n))
      .filter((n) => Number.isFinite(n));

    if (owners.length === 0 && Number.isFinite(Number(joomlaUser?.id))) {
      owners.push(Number(joomlaUser.id));
    }

    const categoryIdNum = Number(form.category_id || 0);
    const mainCategoryIdNum = Number(form.main_category_id || form.category_id || 0);

    const params = {
      user_registration: null as null,
      joomdle_courses: String(form.course_id || propCourseId || ''),
    };

    const proctorUser =
      form.proctor_id != null
        ? proctorUsers.find((u: any) => Number(u.id) === Number(form.proctor_id))
        : undefined;

    const proctorName =
      proctorUser?.username ||
      proctorUser?.email ||
      (typeof form.proctor === 'string' ? form.proctor : '');

    const custom_fields: Record<string, any> = {
      field_hours: String(form.field_hours || ''),
      sl1: String(form.sl1 || ''),
      event_type: String(form.event_type || ''),
      group_id: String((form as any).group_id || ''),
      webex: String(form.webex || ''),
      deal_id: String(form.deal_id || ''),
      use_article_desc: form.use_description_template ? '1' : '0',
      article_id: String(form.template_id || ''),
      proctors: proctorName ? [proctorName] : [],
      proctor: proctorName || '',
      customer_id: customerId,
      opportunity_id: opportunityId,
    };

    const payload: any = {
      owners,
      category_id: categoryIdNum,
      main_category_id: mainCategoryIdNum,
      location_id: Number(form.location_id || 0),
      title: form.title || '',
      alias: toAlias(form.title || ''),
      event_type: 0,
      event_date: form.event_date || '',
      event_end_date: form.event_end_date || '',
      tz_offset: tzId,
      short_description: form.short_description || '',
      description: null,
      event_capacity: Number(form.event_capacity || 0),
      private_booking_count: Number((form as any).private_booking_count || 0),
      waiting_list_capacity: useWaitingList ? Number(form.waiting_list_capacity || 0) : 0,
      created_by: Number(joomlaUser?.id || 0),
      cut_off_date: form.cut_off_date || '',
      registration_type: 1,
      params,
      custom_fields,
      speakers: (form.speakers || []).map((s: any) => s.id),
      sponsors: (form.sponsors || []).map((s: any) => s.id),
      gallery: (form.gallery || []).map((g: any) => g.id),
      agenda: form.agenda || [],
    };

    let result: any;
    if (eventId) {
      payload.id = eventId;
      result = await putItem(`moodle/joomla/event`, payload);
    } else {
      result = await postItem(`moodle/joomla/event`, payload);
    }

    if (result) {
      dispatch(hydrateEvents([result] as any));
      queryClient.invalidateQueries({ queryKey: [CacheKey.EventsByCourse, String(propCourseId ?? '')] });
      onCreated(result);
    }
  }, [
    form,
    timezones,
    joomlaUser,
    proctorUsers,
    customerId,
    opportunityId,
    useWaitingList,
    eventId,
    propCourseId,
    dispatch,
    queryClient,
    onCreated,
  ]);

  useEffect(() => {
    onSaveRegister?.(handleSave);
  }, [handleSave, onSaveRegister]);

  return (
    <>
      <HbrForm>
        <HbrTabGroup size="small">
          <HbrTab
            slot="nav"
            panel="eventInfo"
            active={activePanel === 'eventInfo'}
            onClick={() => setActivePanel('eventInfo')}
          >
            Event Information
          </HbrTab>
          <HbrTab
            slot="nav"
            panel="eventDetails"
            active={activePanel === 'eventDetails'}
            onClick={() => setActivePanel('eventDetails')}
          >
            Event Details
          </HbrTab>
          <HbrTab
            slot="nav"
            panel="additionalInfo"
            active={activePanel === 'additionalInfo'}
            onClick={() => setActivePanel('additionalInfo')}
          >
            Additional Info
          </HbrTab>

          <HbrTabPanel name="eventInfo" active={activePanel === 'eventInfo'} className="pt-4">
            {isLoading ? (
              <div className="flex justify-center items-center h-48">
                <HbrSpinner size="medium" />
              </div>
            ) : (
              <>
                <HbrInput
                  name="title"
                  value={getDefaultTitle()}
                  onInput={handleInput}
                  required
                >
                  <div slot="label">
                    Title <em className="text-gray-500">(required)</em>
                  </div>
                  {renderErrorMessage(userEditedTitle && !form.title, 'Please enter a title')}
                </HbrInput>

                <MoodleUsersSelect
                  multi
                  preselected={preselectedOwners}
                  onChange={handleOwnersChange}
                  label="Event Owners"
                  username={currentUsername}
                />

                <HbrSelect
                  name="proctor_id"
                  label="Proctor"
                  value={selectedProctorId == null ? '' : String(selectedProctorId)}
                  onHbr-change={handleProctorSelectInput}
                  placeholder="Select Proctor"
                >
                  {proctorUsers.map((u: any) => (
                    <HbrOption key={String(u.id)} value={String(u.id)}>
                      {(u?.name || u?.username || u?.email || `User #${u?.id}`) +
                        (u?.email ? ` (${u.email})` : '')}
                    </HbrOption>
                  ))}
                </HbrSelect>

                <HbrDivider />

                <HbrSelect
                  name="category_id"
                  value={form.category_id}
                  onHbr-change={handleSelectChange('category_id')}
                  required
                  placeholder="Select Category"
                >
                  {joomlaEventBookingCategories.map((c) => (
                    <HbrOption key={String((c as any).id)} value={String((c as any).id)}>
                      {(c as any).name}
                    </HbrOption>
                  ))}
                  <div slot="label">
                    Category <em className="text-gray-500">(required)</em>
                  </div>
                </HbrSelect>

                <MoodleLocationsSelect
                  preselected={form.location_id ? Number(form.location_id) : undefined}
                  onChange={handleLocationChange}
                  label="Location"
                />

                <HbrSelect
                  name="sl1"
                  value={form.sl1}
                  onHbr-change={handleSelectChange('sl1')}
                  required
                  placeholder="Select Region"
                >
                  <div slot="label">
                    Region <em className="text-gray-500">(required)</em>
                  </div>
                  <HbrOption value="amer">Amer</HbrOption>
                  <HbrOption value="emea">EMEA</HbrOption>
                  <HbrOption value="apjc">APJC</HbrOption>
                </HbrSelect>

                <HbrDivider />

                <HbrSelect
                  name="timezone"
                  value={form.timezone}
                  onHbr-change={handleSelectChange('timezone')}
                  required
                  disabled={!form.sl1}
                  placeholder="Select Timezone"
                >
                  <div slot="label">
                    Event Location Timezone <em className="text-gray-500">(required)</em>
                  </div>
                  {timezones?.map((tz: string) => (
                    <HbrOption key={tz} value={tz}>
                      {tz}
                    </HbrOption>
                  ))}
                  {!form.sl1 && (
                    <div slot="help-text" className="text-sm mt-2 text-sec-blue">
                      Please select a region first to load timezones.
                    </div>
                  )}
                </HbrSelect>

                <HbrInput
                  name="event_date"
                  label="Event Start (local)"
                  type="datetime-local"
                  value={form.event_date}
                  onInput={handleInput}
                  step={300}
                  required
                />

                <HbrInput
                  name="event_end_date"
                  label="Event End (local)"
                  type="datetime-local"
                  value={form.event_end_date}
                  onInput={handleInput}
                  min={form.event_date}
                  step={300}
                  required
                />

                <HbrSwitch
                  name="use_cutoff"
                  label="Set Event Cutoff"
                  checked={useCutoff}
                  onHbr-change={handleUseCutoffToggle}
                />

                {useCutoff && (
                  <HbrInput
                    name="cut_off_date"
                    label="Cut-off (local)"
                    type="datetime-local"
                    value={form.cut_off_date}
                    onInput={handleInput}
                    step={300}
                  />
                )}

                <HbrDivider />

                <HbrInput
                  name="event_capacity"
                  label="Event Capacity"
                  type="number"
                  value={form.event_capacity}
                  onInput={handleInput}
                />

                <HbrSwitch
                  name="use_waiting_list"
                  label="Enable Waiting List"
                  checked={useWaitingList}
                  onHbr-change={handleUseWaitingListToggle}
                />

                {useWaitingList && (
                  <HbrInput
                    name="waiting_list_capacity"
                    label="Waiting List Capacity"
                    type="number"
                    value={form.waiting_list_capacity}
                    onInput={handleInput}
                  />
                )}

                <HbrDivider />

                <HbrSwitch
                  name="use_description_template"
                  label="Use Description Template"
                  checked={form.use_description_template}
                  onHbr-change={handleUseTemplateToggle}
                />

                {form.use_description_template ? (
                  <>
                    <HbrSelect
                      name="template_id"
                      value={form.template_id}
                      onHbr-change={handleTemplateChange}
                      required
                      placeholder="Select Template"
                    >
                      <div slot="label">
                        Template Selection <em className="text-gray-500">(required)</em>
                      </div>
                      {templateOptions.map((t: any) => (
                        <HbrOption key={String(t.id)} value={String(t.id)}>
                          {t.title}
                        </HbrOption>
                      ))}
                    </HbrSelect>

                    {!!form.template_id && (
                      <div style={{ marginBottom: 16 }}>
                        <label
                          style={{
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: 6,
                          }}
                        >
                          Template Preview
                        </label>

                        <div
                          style={{
                            border: '1px solid #e2e2e2',
                            borderRadius: 8,
                            padding: 12,
                            background: '#fafbfc',
                            maxHeight: 200,
                            overflow: 'auto',
                            marginBottom: 8,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(rewriteTemplateHtml(cachedTemplateHtml)),
                          }}
                        />

                        <StbButton role="default" onClick={() => setShowTemplateModal(true)}>
                          Expand
                        </StbButton>
                      </div>
                    )}
                  </>
                ) : (
                  <HbrTextarea
                    name="short_description"
                    label="Short Description"
                    value={form.short_description}
                    onInput={handleInput}
                  />
                )}
              </>
            )}
          </HbrTabPanel>

          <HbrTabPanel name="eventDetails" active={activePanel === 'eventDetails'}>
            <div style={{ paddingTop: '10px' }}>
              <MoodleAgendaTable
                agenda={form.agenda || []}
                onChange={handleAgendaChange}
                eventStartLocal={form.event_date || nextHour}
                eventEndLocal={form.event_end_date || nextHour}
              />
              <HbrDivider />
              <MoodleSpeakersSelect
                selectedSpeakerIds={(form.speakers || []).map((s: any) => s.id)}
                onChange={(speakersChanged) =>
                  setForm((f: any) => ({ ...f, speakers: speakersChanged }))
                }
                label="Speakers"
              />
            </div>
          </HbrTabPanel>

          <HbrTabPanel name="additionalInfo" active={activePanel === 'additionalInfo'}>
            <div style={{ paddingTop: '10px' }}>
              <HbrInput
                name="deal_id"
                label="Deal ID"
                type="text"
                value={form.deal_id}
                onInput={handleInput}
              />
              <HbrInput
                name="webex"
                label="Webex URL"
                type="text"
                value={form.webex}
                onInput={handleInput}
              />
              <HbrDivider />
              <MoodleGalleryTable gallery={form.gallery || []} onChange={handleGalleryChange} />
            </div>
          </HbrTabPanel>
        </HbrTabGroup>
      </HbrForm>

      <StbModal
        open={showTemplateModal}
        role="information"
        size="large"
        title={
          templateOptions.find((t: any) => String(t.id) === String(form.template_id))?.title ||
          'Template Preview'
        }
        onClose={async () => setShowTemplateModal(false)}
        scrollable={true}
      >
        <div
          style={{
            padding: 16,
            fontFamily: 'var(--hbr-font-body)',
            fontSize: 'var(--hbr-font-size-md)',
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cachedTemplateHtml) }}
        />
      </StbModal>
    </>
  );
}